import React, { useCallback, useEffect, useState } from 'react';
import { approveDualApproval, fetchDualApprovals, rejectDualApproval } from '../../services/walletApi';
import { TreasuryUltraAlert, TreasuryUltraBtn, TreasuryUltraRow, TreasuryUltraShell } from './TreasuryUltraShell';

const ACTION_LABELS = {
  client_refund: 'Client refund',
  owner_payout: 'Owner payout',
  bank_topup: 'Bank top-up',
  owner_withdraw: 'Owner withdraw',
};

export default function DualApprovals({ onQueueChange }) {
  const [items, setItems] = useState([]);
  const [threshold, setThreshold] = useState(10000);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchDualApprovals('pending_second_approval');
      const list = data.items || [];
      setItems(list);
      setThreshold(data.thresholdInr || 10000);
      if (onQueueChange) onQueueChange(list.length);
    } catch {
      setItems([]);
      if (onQueueChange) onQueueChange(0);
    } finally {
      setLoading(false);
    }
  }, [onQueueChange]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (id) => {
    const note = window.prompt('Second approval note (optional):') || '';
    setBusyId(id);
    setMessage('');
    try {
      await approveDualApproval(id, note);
      setMessage('Approved and executed by second admin.');
      await load();
    } catch (err) {
      setMessage(err.message || 'Second approval failed — use a different admin login.');
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (id) => {
    const note = window.prompt('Rejection reason:') || '';
    setBusyId(id);
    try {
      await rejectDualApproval(id, note);
      setMessage('Request rejected.');
      await load();
    } catch (err) {
      setMessage(err.message || 'Reject failed');
      await load();
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <p className="text-sm text-de-muted">Loading dual approvals…</p>;
  }

  return (
    <TreasuryUltraShell
      title="Maker–Checker Approvals"
      subtitle={`Amounts ≥ ₹${Number(threshold).toLocaleString('en-IN')} need a second admin (not the requester)`}
      icon={String.fromCodePoint(0x1F6E1)}
      accent="gold"
      badge={items.length || '0'}
      scrollable={items.length > 2}
      scrollMaxHeightClass="max-h-[min(360px,42vh)]"
      showScrollHint={items.length > 2}
      banner={message ? (
        <TreasuryUltraAlert tone={message.toLowerCase().includes('fail') || message.includes('different') ? 'error' : 'success'}>
          {message}
        </TreasuryUltraAlert>
      ) : null}
    >
      {items.length === 0 ? (
        <TreasuryUltraAlert tone="info">No pending second approvals. High-value moves are protected automatically.</TreasuryUltraAlert>
      ) : (
        items.map((row) => (
          <TreasuryUltraRow
            key={row.id}
            tone="amber"
            title={`${ACTION_LABELS[row.action_type] || row.action_type} · ₹${Number(row.amount).toLocaleString('en-IN')}`}
            meta={`Requested by ${row.requested_by?.slice(0, 8)}… · ${new Date(row.created_at).toLocaleString('en-IN')}`}
            actions={(
              <>
                <TreasuryUltraBtn variant="payout" disabled={busyId === row.id} onClick={() => handleApprove(row.id)}>
                  Second approve
                </TreasuryUltraBtn>
                <TreasuryUltraBtn variant="ghost" disabled={busyId === row.id} onClick={() => handleReject(row.id)}>
                  Reject
                </TreasuryUltraBtn>
              </>
            )}
          />
        ))
      )}
    </TreasuryUltraShell>
  );
}
